<app-page-header subtitle="Materiaalbeheer"></app-page-header>
<div class="container-xl mt-4 mb-4">
    <div class="row g-3 mb-3">
        <div class="col-24 col-md-10">
            <mat-form-field appearance="fill" subscriptSizing="dynamic" class="w-100">
                <mat-label>Zoeken</mat-label>
                <input
                    matInput
                    type="search"
                    placeholder="Zoek op materiaal, grondstof, groep, leverancier..."
                    [formControl]="searchControl"
                    (keydown.escape)="searchControl.setValue('')" />
            </mat-form-field>
        </div>
        <div class="col-24 col-md-10">
            <mat-form-field appearance="fill" subscriptSizing="dynamic" class="w-100">
                <mat-label>Soorten</mat-label>
                <mat-select multiple placeholder="Alle soorten" [formControl]="selectedTypeIdsControl">
                    @for (type of availableMaterialTypes(); track type.id) {
                        <mat-option [value]="type.id">{{ type.name }}</mat-option>
                    }
                </mat-select>
            </mat-form-field>
        </div>
    </div>
    <mat-checkbox [checked]="noRawMaterial()" (change)="noRawMaterial.set($event.checked)">
        Zonder grondstof
    </mat-checkbox>
    <mat-checkbox class="ms-3" [checked]="noMainRawMaterial()" (change)="noMainRawMaterial.set($event.checked)">
        Zonder hoofdgrondstof
    </mat-checkbox>
    @if (Roles.Codalt | hasRole) {
        <mat-checkbox class="ms-3" [checked]="showDeleted()" (change)="showDeleted.set($event.checked)">
            Alleen verwijderde materialen tonen
        </mat-checkbox>
    }
    <h4>Materialen</h4>
    @for (material of materials(); track material.id) {
        <div class="row border mb-2 p-2 rounded bg-light">
            <div class="col-24">
                <div class="row align-items-center mb-2">
                    <div class="col-24 col-md-6">
                        <h5 class="mb-0 d-flex align-items-center gap-2">
                            @if (material | materialProperty: 'color'; as color) {
                                <div class="material-color flex-shrink-0" [style.background-color]="color"></div>
                            }
                            {{ material.name }}
                            <small class="text-muted">{{ material | materialProperty: 'usage' }}</small>
                        </h5>
                    </div>
                    <div class="col-24 col-md-6">
                        <small class="text-muted">
                            Gebruikt in <strong>{{ material.plannings_count }}</strong> planningen
                        </small>
                    </div>
                    <div class="col-auto">
                        @if (material.deleted_at) {
                            <span class="badge bg-danger">Verwijderd</span>
                        } @else {
                            <span class="badge bg-success">Actief</span>
                        }
                        <small class="ms-2">#{{ material.id }}</small>
                    </div>
                </div>
            </div>

            <div class="col-24">
                <div class="table-responsive">
                    <table class="table table-sm table-hover mb-0 table-last-col table-add-row">
                        <thead class="table-light">
                            <tr>
                                <th>Grondstof</th>
                                <th>Type</th>
                                <th>Leverancier</th>
                                <th>Kleur</th>
                                <th class="text-end">OD-nummer</th>
                                <th class="text-end">Verbruik</th>
                                <th class="text-end">Dikte</th>
                                <th class="text-end">Breedte</th>
                                <th class="text-end">Bedekkingsgraad</th>
                                <th>Optioneel</th>
                                <th></th>
                            </tr>
                        </thead>
                        @for (rawMaterial of material.raw_materials; track rawMaterial.id) {
                            <tr [class.table-secondary]="rawMaterial.pivot?.optional === 1">
                                <td>
                                    @if (rawMaterial.color) {
                                        <div
                                            class="material-color d-inline-block align-middle me-1"
                                            [style.background-color]="rawMaterial.color.color"></div>
                                    }
                                    <strong>{{ rawMaterial.name }}</strong>
                                    <small class="ms-2">#{{ rawMaterial.id }} - #{{ rawMaterial.pivot?.id }}</small>
                                    @if (rawMaterial.deleted_at) {
                                        <span class="badge bg-danger-subtle text-danger ms-2">Verwijderd</span>
                                    }
                                    @if (rawMaterial.pivot?.deleted_at) {
                                        <span class="badge bg-danger-subtle text-danger ms-2">Link verwijderd</span>
                                    }
                                    @if (rawMaterial.material_type?.main) {
                                        <span class="badge bg-primary ms-2">Hoofd</span>
                                    }
                                </td>
                                <td>
                                    <small>
                                        {{ rawMaterial.material_type?.name }}
                                    </small>
                                </td>
                                <td>
                                    <small>{{ rawMaterial.supplier?.name }}</small>
                                </td>
                                <td>
                                    @if (rawMaterial.color) {
                                        <div class="d-flex align-items-center gap-2">
                                            <div
                                                class="material-color"
                                                [style.background-color]="rawMaterial.color.color"></div>
                                            <small>
                                                {{ rawMaterial.color.name
                                                }}{{ rawMaterial.color.ral ? ' (' + rawMaterial.color.ral + ')' : '' }}
                                            </small>
                                        </div>
                                    } @else {
                                        <small class="text-muted">-</small>
                                    }
                                </td>
                                <td class="text-end">
                                    <small>{{ rawMaterial.od_number ?? '-' }}</small>
                                </td>
                                <td class="text-end text-input" (click)="pivotConsumptionInput.focus()">
                                    <div class="d-flex align-items-center justify-content-end gap-1 h-100">
                                        <input
                                            #pivotConsumptionInput
                                            type="number"
                                            step="0.001"
                                            [placeholder]="rawMaterialDefault(rawMaterial, 'consumption')"
                                            [value]="rawMaterial.pivot?.consumption"
                                            (change)="
                                                updatePivotField(
                                                    rawMaterial.pivot,
                                                    'consumption',
                                                    pivotConsumptionInput.value
                                                )
                                            " />
                                        <small>{{ consumptionTypeName(rawMaterial) }}</small>
                                    </div>
                                </td>
                                <td class="text-end text-input" (click)="pivotThicknessInput.focus()">
                                    <div class="d-flex align-items-center justify-content-end gap-1 h-100">
                                        <input
                                            #pivotThicknessInput
                                            type="number"
                                            step="0.01"
                                            [placeholder]="rawMaterialDefault(rawMaterial, 'thickness')"
                                            [value]="rawMaterial.pivot?.thickness"
                                            (change)="
                                                updatePivotField(
                                                    rawMaterial.pivot,
                                                    'thickness',
                                                    pivotThicknessInput.value
                                                )
                                            " />
                                        <small>mm</small>
                                    </div>
                                </td>
                                <td class="text-end text-input" (click)="pivotWidthInput.focus()">
                                    <div class="d-flex align-items-center justify-content-end gap-1 h-100">
                                        <input
                                            #pivotWidthInput
                                            type="number"
                                            step="0.01"
                                            [placeholder]="rawMaterialDefault(rawMaterial, 'width')"
                                            [value]="rawMaterial.pivot?.width"
                                            (change)="
                                                updatePivotField(rawMaterial.pivot, 'width', pivotWidthInput.value)
                                            " />
                                        <small>cm</small>
                                    </div>
                                </td>
                                <td class="text-end text-input" (click)="pivotCoverageRateInput.focus()">
                                    <div class="d-flex align-items-center justify-content-end gap-1 h-100">
                                        <input
                                            #pivotCoverageRateInput
                                            appDecimalInput
                                            type="text"
                                            inputmode="numeric"
                                            [placeholder]="rawMaterialDefault(rawMaterial, 'coverage_rate')"
                                            [value]="rawMaterial.pivot?.coverage_rate"
                                            (change)="
                                                updatePivotField(
                                                    rawMaterial.pivot,
                                                    'coverage_rate',
                                                    pivotCoverageRateInput.value
                                                )
                                            " />
                                        <small>%</small>
                                    </div>
                                </td>
                                <td>
                                    @if (rawMaterial.pivot?.optional === 1) {
                                        <span
                                            class="badge bg-warning-subtle text-warning cursor-pointer"
                                            (contextmenu)="toggleOptional($event, rawMaterial.pivot)"
                                            title="Rechtsklik om te wisselen tussen optioneel en verplicht">
                                            Optioneel
                                        </span>
                                    } @else {
                                        <span
                                            class="badge bg-success-subtle text-success cursor-pointer"
                                            (contextmenu)="toggleOptional($event, rawMaterial.pivot)"
                                            title="Rechtsklik om te wisselen tussen optioneel en verplicht">
                                            Verplicht
                                        </span>
                                    }
                                </td>
                                <td>
                                    <button
                                        class="btn btn-sm btn-outline-danger"
                                        (click)="editRawMaterial(material, rawMaterial)"
                                        title="Verwijderen">
                                        <i class="fas fa-trash"></i>
                                    </button>
                                </td>
                            </tr>
                        }
                        <tr class="add-row">
                            <td colspan="11">
                                <button
                                    class="w-100"
                                    (click)="addChooseRawMaterial(material)"
                                    color="primary"
                                    mat-button>
                                    Grondstof toevoegen <i class="fas fa-plus"></i>
                                </button>
                            </td>
                        </tr>
                    </table>
                </div>
            </div>
        </div>
    } @empty {
        <p class="text-muted">Geen materialen</p>
    }

    <hr class="my-4" />
    <h4>Alle grondstoffen</h4>
    <div class="table-responsive">
        <table class="table table-sm table-hover table-striped mb-0">
            <thead class="table-light">
                <tr>
                    <th>#</th>
                    <th>Naam</th>
                    <th>Type</th>
                    <th>Leverancier</th>
                    <th>Kleur</th>
                    <th class="text-end">OD-nummer</th>
                    <th class="text-end">Verbruik</th>
                    <th class="text-end">Dikte</th>
                    <th class="text-end">Breedte</th>
                    <th class="text-end">Bedekkingsgraad</th>
                    <th>Certificaat</th>
                </tr>
            </thead>
            <tbody>
                @for (rm of allRawMaterials(); track rm.id) {
                    <tr [class.table-danger]="rm.deleted_at" [formGroup]="$any(rawMaterialForms().get(rm.id))">
                        <td>{{ rm.id }}</td>
                        <td>
                            <div class="d-flex gap-2 align-items-center">
                                @if (rm.color) {
                                    <div class="material-color" [style.background-color]="rm.color.color"></div>
                                }
                                <strong>{{ rm.name }}</strong>
                                @if (rm.deleted_at) {
                                    <span class="badge bg-danger-subtle text-danger ms-2">Verwijderd</span>
                                }
                            </div>
                        </td>
                        <td>
                            <small>{{ rm.material_type?.name ?? '-' }}</small>
                            @if (rm.material_type?.main) {
                                <span class="badge bg-primary ms-1">Hoofd</span>
                            }
                        </td>
                        <td class="supplier-select-cell">
                            <mat-select formControlName="supplier_id" panelWidth="auto" placeholder="-">
                                <mat-option [value]="null">-</mat-option>
                                @for (supplier of allSuppliers(); track supplier.id) {
                                    <mat-option [value]="supplier.id">
                                        <small>{{ supplier.name }}</small>
                                    </mat-option>
                                }
                            </mat-select>
                        </td>
                        <td class="color-select-cell">
                            <mat-select formControlName="color_id" panelWidth="auto">
                                <mat-select-trigger>
                                    @if (rm.color) {
                                        <div class="d-flex align-items-center gap-2">
                                            <div class="material-color" [style.background-color]="rm.color.color"></div>
                                            <small>
                                                {{ rm.color.name }}
                                                {{ rm.color.ral ? ' (' + rm.color.ral + ')' : '' }}
                                            </small>
                                        </div>
                                    } @else {
                                        <small class="text-muted">-</small>
                                    }
                                </mat-select-trigger>
                                <mat-option [value]="null">-</mat-option>
                                @for (color of allColors(); track color.id) {
                                    <mat-option [value]="color.id">
                                        <div class="d-flex align-items-center gap-2">
                                            <div class="material-color" [style.background-color]="color.color"></div>
                                            <small>{{ color.name }}{{ color.ral ? ' (' + color.ral + ')' : '' }}</small>
                                        </div>
                                    </mat-option>
                                }
                            </mat-select>
                        </td>
                        <td class="text-end">
                            <small>{{ rm.od_number ?? '-' }}</small>
                        </td>
                        <td
                            class="text-end text-input"
                            (click)="consumptionInput.focus()"
                            (contextmenu)="cycleConsumptionType($event, rm)"
                            title="Rechtsklik om verbruikseenheid te wijzigen">
                            <div class="d-flex align-items-center justify-content-end gap-1 h-100">
                                <input
                                    #consumptionInput
                                    type="number"
                                    step="0.001"
                                    placeholder="-"
                                    formControlName="consumption" />
                                <small>{{ rm.consumption_type?.name }}</small>
                            </div>
                        </td>
                        <td class="text-end text-input" (click)="thicknessInput.focus()">
                            <div class="d-flex align-items-center justify-content-end gap-1 h-100">
                                <input
                                    #thicknessInput
                                    type="number"
                                    step="0.01"
                                    placeholder="-"
                                    formControlName="thickness" />
                                <small>mm</small>
                            </div>
                        </td>
                        <td class="text-end text-input" (click)="widthInput.focus()">
                            <div class="d-flex align-items-center justify-content-end gap-1 h-100">
                                <input #widthInput type="number" step="0.01" placeholder="-" formControlName="width" />
                                <small>cm</small>
                            </div>
                        </td>
                        <td class="text-end text-input" (click)="coverageRateInput.focus()">
                            <div class="d-flex align-items-center justify-content-end gap-1 h-100">
                                <input
                                    #coverageRateInput
                                    appDecimalInput
                                    type="text"
                                    inputmode="numeric"
                                    placeholder="-"
                                    formControlName="coverage_rate" />
                                <small>%</small>
                            </div>
                        </td>
                        <td>
                            <small>{{ rm.certificate_number ?? '-' }}</small>
                        </td>
                    </tr>
                }
            </tbody>
        </table>
    </div>
</div>
